import Dropdown from '@/components/Dropdown'
import MultiSelectCheckbox from '@/components/MultiSelectCheckbox'
import useConfig from '@/hooks/use-config'
import "@/translations/i18n"
import rem from '@/utils/rem'
import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from "react-i18next"
import useBehalfPanel from '@/hooks/use-behalf-panel'
import { useFeature } from '@/hooks/use-feature/index'
import { featureConstant } from '@/constants/configuration/feature'
import { filterOnBehalfOfByPrincipals } from '@/hooks/use-ap-flow-detection/pull-participant-list-builder'
import PropTypes from 'prop-types'

const AdditionalZone = ({ on_behalf_of, flow, callback, showOrLogic = false, showPrincipalsOnly = false, principalsList = [], selectedPrincipals = [], onPrincipalsChange, callbackInput }) => { 

    const {t} = useTranslation();
    const isAgentPrincipalUiPullEnabled = useFeature(featureConstant.AGENT_PRINCIPAL_UI_PULL);

    const [options, setOptions] = useState([{ name: t("select") }])
    const [selected, setSelected] = useState({ name: t("select") })
    const [ behalfPanel, setBehalfPanel ] = useBehalfPanel(on_behalf_of)
    const { fetch: fetchConfig } = useConfig()

    useEffect(() => {
        if (!isAgentPrincipalUiPullEnabled) return;

        fetchConfig()

        const filteredOnBehalfOf = selectedPrincipals.length > 0 ? filterOnBehalfOfByPrincipals(on_behalf_of, selectedPrincipals) : on_behalf_of;
        
        setOptions([{ name: t("select"), id: 'no' }, ...filteredOnBehalfOf])

    }, [selectedPrincipals, showOrLogic, on_behalf_of])

	useEffect(() => {
        if(isAgentPrincipalUiPullEnabled) return;
        fetchConfig()
        setOptions([{ name: t("select"), id: 'no' }, ...on_behalf_of])
	}, [])

	useEffect(() => {
        if (selected) {
            callback(selected)
        }
	}, [selected])

    const isOnBehalfOfSelected = isAgentPrincipalUiPullEnabled && selected?.id && selected.id !== 'no';

    const principalsWithSelection = useMemo(() => {
        return principalsList.map(principal => ({
            ...principal,
            id: principal.id || principal.uen,
            label: principal.name + (principal.uen ? ` UEN: ${principal.uen}` : ''),
            $selected: selectedPrincipals.some(p => p.id === principal.id || p.uen === principal.uen)
        }));
    }, [principalsList, selectedPrincipals]);

    const handlePrincipalsChange = (updatedItems) => {
        if (!onPrincipalsChange) return;
        const newSelectedPrincipals = updatedItems
            .filter(item => item.$selected)
            .map(item => principalsList.find(p => (p.id || p.uen) === item.id))
            .filter(Boolean);
        onPrincipalsChange(newSelectedPrincipals);
    };

    const renderPrincipalsDropdown = () => (
		<div
			className="additional-zone__item__principals"
            data-disabled={isOnBehalfOfSelected}
		>
			<div className="additional-box__header__title">
				{t("selectMyPrincipals")}
			</div>
			<MultiSelectCheckbox
				items={principalsWithSelection}
				onChangeFunc={handlePrincipalsChange}
				disable={isOnBehalfOfSelected}
				label={t("select")}
			/>
		</div>
	);

    if (isAgentPrincipalUiPullEnabled && showPrincipalsOnly && principalsList.length > 0) {
        return (
            <>
                <div className="t--share-data-subheader">
                    {t("additionalInformation")}
                </div>
                <div className="additional-zone">
                    {renderPrincipalsDropdown()}
                </div>
            </>
        );
    }

    if (isAgentPrincipalUiPullEnabled && showOrLogic && principalsList.length > 0) {
        return (
            <>
                <div className="t--share-data-subheader">
                    {t("additionalInformation")}
                </div>
                <div className="additional-zone">
                    <div className="additional-zone__item" style={{ marginLeft: 0, marginRight: 0 }}>
                        <div className="additional-box__header__title">
                            {t("onBehalfOf")}
                        </div>
                        <div className="additional-box__header__input">
                            <Dropdown 
                                value={selected?.name} 
                                style={{ minWidth: rem(150) }}
                                disabled={selectedPrincipals.length > 0}
                            >
                                {options.map((option) => (
                                    <Dropdown.Item 
                                        key={option.id ?? option.name}
                                        onClick={() => {
                                            if (selectedPrincipals.length === 0) {
                                                setSelected(option);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                if (selectedPrincipals.length === 0) {
                                                    setSelected(option);
                                                }
                                            }
                                        }}
                                    >
                                        {option.name}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown>
                        </div>
                    </div>
                    <div className="divider">
                        <span>or</span>
                    </div>
                    {renderPrincipalsDropdown()}
                </div>
            </>
        );
    }

    return ((flow === 'share' || behalfPanel) && (
        <>
            <div className="t--share-data-subheader">
                {t("additionalInformation")}
            </div>
            <div className="additional-zone">
                {behalfPanel && (<div className="additional-zone__item">
                        <div className="additional-box__header__title">
                            {t("onBehalfOf")} *
                        </div>
                        <div className="additional-box__header__input">
                            <Dropdown value={selected?.name} style={{ minWidth: rem(150) }}>
                                {options.map((option, index) => {
                                    return (
                                        <Dropdown.Item key={index}>
                                            <div onClick={() => setSelected(option)}>{option.name}</div>
                                        </Dropdown.Item>
                                    )
                                })}
                            </Dropdown>
                        </div>
                </div>)}
                {flow === 'share' && (<div className="additional-zone__item">
                        <div className="additional-box__header__title">
                            {t("referenceId")}
                        </div>
                        <div className="additional-box__header__input">
                            <input onChange={event => callbackInput(event.target.value)}/>
                        </div>
                </div>)}
            </div>
        </>)
    )
}

AdditionalZone.propTypes = {
    on_behalf_of: PropTypes.array,
    flow: PropTypes.string,
    callback: PropTypes.func,
    callbackInput: PropTypes.func,
    showOrLogic: PropTypes.bool,
    showPrincipalsOnly: PropTypes.bool,
    principalsList: PropTypes.array,
    selectedPrincipals: PropTypes.array,
    onPrincipalsChange: PropTypes.func,
};

export default AdditionalZone;
